// MyProfile — `/me/profile`
//
// iter65: Own profile view. Mirrors FriendProfile layout (MediaCarousel hero,
// name + meta, optional staying-at), with an Edit Profile modal for display
// name, photo upload, and Instagram. Backend PATCH /users/me handles persistence.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Edit2, X, Building2, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import MediaCarousel from "../components/MediaCarousel";

export default function MyProfile() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [savedLocations, setSavedLocations] = useState([]);

  const load = async () => {
    const me = await getMe();
    if (!me) { nav("/login"); return; }
    setUser(me);
    setLoading(false);
    api.get("/me/saved-locations").then(({ data }) => setSavedLocations(data || [])).catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      if (!me) { nav("/login"); return; }
      setUser(me);
      setLoading(false);
      api.get("/me/saved-locations").then(({ data }) => { if (!cancelled) setSavedLocations(data || []); }).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [nav]);

  if (loading || !user) {
    return (
      <main className="min-h-screen bg-background grid place-items-center text-foreground/55" data-testid="me-profile-loading">
        {t("me_profile_loading")}
      </main>
    );
  }

  const name = user.display_name || "—";
  const initials = (name || "?").slice(0, 2).toUpperCase();
  const photos = user.profile_photo
    ? [{ full: user.profile_photo, medium: user.profile_photo }]
    : [];
  const hotel = (savedLocations || []).find((s) => s.category === "hotel");

  return (
    <main className="min-h-screen bg-background pb-24" data-testid="me-profile-page">
      <MediaCarousel photos={photos} initials={initials} testIdPrefix="me-hero" />

      {/* Floating back button */}
      <button
        type="button"
        onClick={() => nav(-1)}
        aria-label="Back"
        data-testid="me-profile-back"
        className="fixed top-4 left-4 z-30 grid place-items-center rounded-full"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", color: "#F5F1E8", width: 44, height: 44, minHeight: 48, minWidth: 48 }}
      >
        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Edit button */}
      <button
        type="button"
        onClick={() => setEditOpen(true)}
        data-testid="me-profile-edit-btn"
        className="fixed top-4 right-4 z-30 grid place-items-center rounded-full transition-colors hover:bg-[rgba(232,184,75,0.12)]"
        style={{
          width: 44,
          height: 44,
          minHeight: 48,
          minWidth: 48,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(8px)",
          color: "#E8B84B",
          border: "1px solid rgba(232,184,75,0.45)",
        }}
        aria-label={t("me_profile_edit")}
      >
        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <section className="px-5 pt-5">
        <h1
          data-testid="me-profile-name"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: "#F5F1E8", lineHeight: 1.15 }}
        >
          {name}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {user.language && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-foreground/65"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}
            >
              <span aria-hidden>{({ en: "🇬🇧", es: "🇪🇸" })[user.language] || "🏳️"}</span>
              <span className="uppercase tracking-wide">{user.language}</span>
            </span>
          )}
          {user.instagram && (
            <a
              href={`https://instagram.com/${user.instagram.replace(/^@/, "")}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="me-profile-instagram"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-foreground hover:text-gold transition-colors"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}
            >
              @{user.instagram.replace(/^@/, "")}
            </a>
          )}
        </div>

        {/* Share my profile */}
        <button
          type="button"
          data-testid="me-profile-share-btn"
          onClick={async () => {
            const link = `${window.location.origin}/friends/${user.user_id}`;
            try {
              await navigator.clipboard.writeText(link);
              toast.success(t("me_profile_share_copied"));
            } catch {
              // Fallback: textarea trick
              const ta = document.createElement("textarea");
              ta.value = link;
              document.body.appendChild(ta);
              ta.select();
              try { document.execCommand("copy"); toast.success(t("me_profile_share_copied")); } catch { /* ignore */ }
              document.body.removeChild(ta);
            }
          }}
          className="mt-4 inline-flex items-center justify-center gap-2 transition-colors hover:bg-[rgba(232,184,75,0.06)]"
          style={{
            minHeight: 48,
            padding: "0 18px",
            border: "1px solid rgba(232,184,75,0.5)",
            color: "#E8B84B",
            borderRadius: 12,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            background: "transparent",
          }}
        >
          <Share2 className="w-4 h-4" strokeWidth={1.5} />
          {t("me_profile_share")}
        </button>
      </section>

      {hotel && (
        <section className="px-5 mt-8" data-testid="me-staying-at">
          <div className="eyebrow mb-3" style={{ color: "#E8B84B", letterSpacing: "1.5px" }}>
            {lang === "es" ? "ALOJAMIENTO" : "STAYING AT"}
          </div>
          <div
            style={{
              background: "#141414",
              border: "0.5px solid #222",
              borderRadius: 14,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              minHeight: 64,
            }}
          >
            <Building2 className="shrink-0" style={{ width: 22, height: 22, color: "#E8B84B" }} strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, color: "#F5F1E8", lineHeight: 1.2 }} className="truncate">
                {hotel.name}
              </div>
              {(hotel.neighborhood || hotel.address) && (
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", lineHeight: 1.3, marginTop: 2 }} className="truncate">
                  {hotel.neighborhood || hotel.address}
                </div>
              )}
            </div>
            <MapPin className="shrink-0" style={{ width: 18, height: 18, color: "#9A9490" }} strokeWidth={1.5} />
          </div>
        </section>
      )}

      <EditProfileModal
        key={editOpen ? `${user.user_id}-${user.display_name}` : "closed"}
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
        onSaved={() => { setEditOpen(false); load(); }}
        t={t}
      />
    </main>
  );
}

/* ---------------- Edit modal ---------------- */
function EditProfileModal({ open, onOpenChange, user, onSaved, t }) {
  const [name, setName] = useState(user.display_name || "");
  const [instagram, setInstagram] = useState(user.instagram || "");
  const [photoUrl, setPhotoUrl] = useState(user.profile_photo || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!open) return null;

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("image", file);
      const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.medium || data.thumbnail || data.full;
      if (url) setPhotoUrl(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        display_name: name.trim() || undefined,
        instagram: instagram.trim().replace(/^@/, "") || null,
        profile_photo: photoUrl || null,
      };
      await api.patch("/users/me", payload);
      toast.success(t("me_profile_saved_toast"));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        onClick={() => !busy && onOpenChange(false)}
        className="fixed inset-0 z-[80]"
        style={{ background: "rgba(0,0,0,0.85)" }}
      />
      <div
        data-testid="edit-profile-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[81] w-[92vw] max-w-md p-6"
        style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 20, maxHeight: "90vh", overflowY: "auto" }}
      >
        <button
          type="button"
          onClick={() => !busy && onOpenChange(false)}
          data-testid="edit-profile-close"
          aria-label="Close"
          className="absolute top-3 right-3 grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 32, height: 32, color: "#B0AAA3" }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1.2, paddingRight: 32 }}>
          {t("me_profile_edit")}
        </h2>

        {/* Photo */}
        <div className="mt-5">
          <div className="eyebrow mb-2">{t("me_profile_field_photo")}</div>
          <div className="flex items-center gap-3">
            <div
              className="rounded-full overflow-hidden shrink-0"
              style={{ width: 64, height: 64, background: "#0B0B0B", border: "1px solid rgba(232,184,75,0.4)" }}
            >
              {photoUrl ? (
                <img src={photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center" style={{ fontFamily: "'Playfair Display', serif", color: "#E8B84B", fontSize: 24 }}>
                  {(name || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <label
              className="inline-flex items-center justify-center cursor-pointer transition-colors hover:bg-[rgba(232,184,75,0.06)]"
              style={{
                minHeight: 48,
                padding: "0 16px",
                border: "1px solid rgba(232,184,75,0.5)",
                color: "#E8B84B",
                borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {uploading ? "…" : (photoUrl ? t("me_profile_field_photo_change") : t("me_profile_field_photo_upload"))}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                data-testid="edit-profile-photo-input"
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Name */}
        <div className="mt-5">
          <div className="eyebrow mb-2">{t("me_profile_field_name")}</div>
          <input
            data-testid="edit-profile-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input w-full"
            style={{ minHeight: 48, fontSize: 16, background: "#0B0B0B", color: "#F5F1E8", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0 14px", fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>

        {/* Instagram */}
        <div className="mt-5">
          <div className="eyebrow mb-2">{t("me_profile_field_instagram")}</div>
          <input
            data-testid="edit-profile-instagram-input"
            type="text"
            value={instagram}
            onChange={(e) => setInstagram(e.target.value.replace(/^@/, ""))}
            placeholder="username"
            className="form-input w-full"
            style={{ minHeight: 48, fontSize: 16, background: "#0B0B0B", color: "#F5F1E8", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0 14px", fontFamily: "'DM Sans', sans-serif" }}
          />
          <div className="mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490" }}>
            {t("me_profile_field_instagram_hint")}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="edit-profile-cancel"
            className="transition-colors hover:bg-[rgba(232,184,75,0.06)] disabled:opacity-40"
            style={{
              minHeight: 48,
              background: "transparent",
              border: "1px solid rgba(232,184,75,0.5)",
              color: "#E8B84B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderRadius: 12,
            }}
          >
            {t("me_profile_cancel")}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || uploading}
            data-testid="edit-profile-save"
            className="hover:opacity-90 transition-opacity disabled:opacity-40"
            style={{
              minHeight: 48,
              background: "#E8B84B",
              color: "#0B0B0B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderRadius: 12,
              border: "1px solid #E8B84B",
            }}
          >
            {t("me_profile_save")}
          </button>
        </div>
      </div>
    </>
  );
}
